import { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ClipboardCheck, ChevronDown, ChevronUp } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";



// ---------------------------------------------------------------------------
// CloseAtendimentoBlock — action that lets the Atendimento explicitly close
// their phase. Requires the Zoho status to be set (any synchronised value).
// Once closed, the lead advances to the Consultor de Cuidados phase (RT
// scheduling flow).
//
// ``variant`` prop:
//   * "inline"   (default) — legacy right-sidebar card
//   * "floating" — bottom-left FAB, mirrors the RT FAB on the right. In
//                  this mode the closed-state confirmation is not shown
//                  (the FAB simply hides so it doesn't compete with RT).
// ---------------------------------------------------------------------------
export default function CloseAtendimentoBlock({
  lead, onChanged, zohoStatuses, zohoStatusesLoading, onZohoStatusSaved,
  variant = "inline",
}) {
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [pickerStatus, setPickerStatus] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);
  // 20/02/2026 — Once the atendimento has been closed the block turns
  // into a passive confirmation. Collapsed by default in that state so
  // it doesn't push the sidebar down for the whole team.
  const [expanded, setExpanded] = useState(false);

  const closedAt = (lead?.atendimento_closed_at || "").trim();
  const isClosed = Boolean(closedAt);
  const hasStatus = Boolean((lead?.zoho_lead_status || "").trim());
  const converted = Boolean(lead?.zoho_converted);
  const isFloating = variant === "floating";

  // The Atendimento phase remains "open" until this timestamp is set.
  // While it is open we let the operator close it explicitly; after
  // that we render a confirmation card with the timestamp/status.
  if (isClosed) {
    // Floating variant hides itself once closed — the RT FAB already
    // covers the next-phase action and we don't want two floating chips
    // competing for the same real estate.
    if (isFloating) return null;
    let when = closedAt;
    try {
      when = new Date(closedAt).toLocaleString("pt-PT", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch (e) { /* keep raw */ }
    return (
      <div
        data-testid="close-atendimento-done"
        className="bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/40 rounded-2xl"
      >
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="close-atendimento-done-toggle"
          className="w-full flex items-center gap-3 p-4 text-left hover:bg-[hsl(var(--success-bg))]/50 rounded-2xl transition-colors"
          aria-expanded={expanded}
        >
          <div className="w-8 h-8 rounded-full bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/40 flex items-center justify-center flex-shrink-0">
            <CheckCircle2 className="w-4 h-4 text-[hsl(var(--success))]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[hsl(var(--success))]">
              Atendimento fechado
            </div>
            <div className="text-xs text-[hsl(var(--success))] truncate">
              {when}{lead?.zoho_lead_status ? <> · <b>{lead.zoho_lead_status}</b></> : null}
            </div>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-[hsl(var(--success))] flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-[hsl(var(--success))] flex-shrink-0" />}
        </button>
        {expanded && (
          <div className="px-4 pb-4 pt-1 text-xs text-[hsl(var(--success))]/90 leading-relaxed border-t border-[hsl(var(--success))]/25">
            <div className="mt-3">
              <span className="text-[hsl(var(--success))] font-semibold">Fechado a:</span> {when}
            </div>
            {lead?.zoho_lead_status && (
              <div className="mt-1.5">
                <span className="text-[hsl(var(--success))] font-semibold">Estado Zoho:</span> {lead.zoho_lead_status}
              </div>
            )}
            <div className="mt-2 text-[hsl(var(--success))]/80">
              O Consultor de Cuidados pode agora agendar a Reunião Técnica.
            </div>
          </div>
        )}
      </div>
    );
  }

  const doClose = async () => {
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/close-atendimento`);
      toast.success("Atendimento fechado — lead pronto para agendar RT");
      setModalOpen(false);
      onChanged?.();
    } catch (e) {
      const msg = e.response?.data?.detail || "Erro a fechar Atendimento";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const saveZohoStatusAndClose = async () => {
    if (!pickerStatus) {
      toast.error("Escolha um estado no Zoho antes de continuar");
      return;
    }
    setSavingStatus(true);
    try {
      const r = await api.post(`/leads/${lead.id}/zoho-status`, { zoho_status: pickerStatus });
      onZohoStatusSaved?.(r.data);
      toast.success(`Estado Zoho atualizado: ${pickerStatus}`);
      // Small chained call — after the status is persisted the modal
      // moves straight into closing so the operator finishes both
      // actions in one gesture.
      setSavingStatus(false);
      setBusy(true);
      try {
        await api.post(`/leads/${lead.id}/close-atendimento`);
        toast.success("Atendimento fechado — lead pronto para agendar RT");
        setModalOpen(false);
        onChanged?.();
      } catch (e2) {
        toast.error(e2.response?.data?.detail || "Erro a fechar Atendimento");
      } finally {
        setBusy(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao atualizar estado Zoho");
      setSavingStatus(false);
    }
  };

  const handleClick = () => {
    if (converted) {
      toast.error("Lead já foi convertido no Zoho — não é possível fechar aqui.");
      return;
    }
    if (!hasStatus) {
      setPickerStatus("");
      setModalOpen(true);
      return;
    }
    doClose();
  };

  return (
    <>
      {isFloating ? (
        <button
          data-testid="btn-close-atendimento-fab"
          onClick={handleClick}
          disabled={busy || converted}
          title="Fechar Atendimento"
          aria-label="Fechar Atendimento"
          className="fixed top-[calc(50%+50px)] right-0 z-40 flex flex-col items-center gap-2 py-4 px-2 rounded-l-2xl bg-card border border-r-0 border-border shadow-[0_10px_40px_-10px_rgba(15,27,44,0.14)] hover:bg-muted transition-colors group disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin text-foreground" />
          ) : (
            <ClipboardCheck className="w-4 h-4 text-foreground" />
          )}
          {!hasStatus && (
            <span className="inline-flex items-center justify-center w-[22px] h-[22px] rounded-full bg-[hsl(var(--warning))] text-[hsl(var(--warning-bg))] text-[10px] font-bold" title="Falta definir estado Zoho">
              !
            </span>
          )}
          <span className="[writing-mode:vertical-rl] rotate-180 text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground group-hover:text-foreground">
            Fechar Atendimento
          </span>
        </button>
      ) : (
        <div
          data-testid="close-atendimento-block"
          className="bg-card border border-border rounded-2xl p-5 shadow-sm"
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="w-9 h-9 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0">
              <ClipboardCheck className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-primary mb-0.5">
                Fase Atendimento
              </div>
              <h3 className="font-ui text-base text-foreground leading-tight">
                Fechar Atendimento
              </h3>
              <p className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed">
                Termina esta fase e sinaliza que o lead está pronto para agendar Reunião Técnica.
                É obrigatório definir o estado no Zoho antes de fechar.
              </p>
            </div>
          </div>
          <button
            data-testid="btn-close-atendimento"
            onClick={handleClick}
            disabled={busy || converted}
            className="w-full inline-flex items-center justify-center gap-2 bg-foreground hover:bg-foreground/90 text-background rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Fechar Atendimento
          </button>
          {!hasStatus && !converted && (
            <div
              data-testid="close-atendimento-warn-no-status"
              className="mt-3 text-[11px] text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/25 rounded-lg px-3 py-2 leading-relaxed"
            >
              ⚠ O estado no Zoho ainda não foi definido — vai ser pedido no próximo passo.
            </div>
          )}
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={(open) => { if (!savingStatus && !busy) setModalOpen(open); }}>
        <DialogContent data-testid="close-atendimento-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Definir estado Zoho antes de fechar</DialogTitle>
            <DialogDescription>
              O Atendimento não pode ser fechado sem um estado sincronizado no Zoho CRM.
              Escolha um estado da picklist actual e nós fechamos a fase de seguida.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
              Estado (Zoho CRM)
            </label>
            <select
              data-testid="close-atendimento-status-select"
              value={pickerStatus}
              onChange={(e) => setPickerStatus(e.target.value)}
              disabled={savingStatus || busy || zohoStatusesLoading}
              className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
            >
              <option value="">
                {zohoStatusesLoading ? "A carregar picklist…" : (zohoStatuses?.length ? "— escolher —" : "— sem picklist —")}
              </option>
              {(zohoStatuses || []).map((s) => (
                <option key={s.actual_value} value={s.actual_value}>{s.display_value}</option>
              ))}
            </select>
            <div className="text-[11px] text-muted-foreground leading-relaxed">
              Se seleccionar um estado de desqualificação, deve depois registar
              o motivo no fluxo normal — este atalho não cobre esse caso.
            </div>
          </div>
          <DialogFooter className="pt-4">
            <button
              data-testid="close-atendimento-cancel"
              onClick={() => setModalOpen(false)}
              disabled={savingStatus || busy}
              className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-4 py-2 font-semibold disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              data-testid="close-atendimento-confirm"
              onClick={saveZohoStatusAndClose}
              disabled={savingStatus || busy || !pickerStatus}
              className="inline-flex items-center gap-2 bg-foreground hover:bg-foreground/90 text-background rounded-full px-4 py-2 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {(savingStatus || busy) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Gravar e fechar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


// CallControls + CallEndDialog moved to @/components/CallControls




